import React, { useState } from 'react';
import { Button, Input, Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  groupName: string;
  setGroupName: (name: string) => void;
  groupDescription: string;
  setGroupDescription: (description: string) => void;
  creating: boolean;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  groupName,
  setGroupName,
  groupDescription,
  setGroupDescription,
  creating,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Create Requirement Folder</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={creating}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto">
            <div className="w-full space-y-6">
              <Input
                id="group-name"
                name="group-name"
                label="Folder Name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., User Authentication System"
                required
                autoFocus
                className="w-full"
              />
              
              <div>
                <label className="block text-bg-v2-sm font-bg-v2-medium text-text-secondary mb-bg-v2-sm">
                  Description (Optional)
                </label>
                <textarea
                  value={groupDescription}
                  onChange={(e) => setGroupDescription(e.target.value)}
                  className="w-full px-bg-v2-lg py-bg-v2-md border border-border-primary bg-bg-primary rounded-bg-v2-md focus:outline-none focus:ring-2 focus:ring-focus-ring resize-none text-bg-v2-md"
                  rows={3}
                  placeholder="Brief description of this requirement folder"
                />
              </div>
            </div>
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={creating}
            />
            <Button
              variant="primary"
              type="submit"
              label={creating ? 'Creating...' : 'Create Folder'}
              disabled={!groupName.trim() || creating}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

